import { Suspense, lazy, useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { focusManager, useQueryClient } from '@tanstack/react-query';
import NotificationsInbox from './components/NotificationsInbox.jsx';
import PullToRefresh from './components/PullToRefresh.jsx';
import { NOTIFICATIONS_KEY } from './hooks/useNotifications.js';
import Header from './components/Header.jsx';
import Footer from './components/Footer.jsx';
import BottomNav from './components/BottomNav.jsx';
import DriverShell from './components/DriverShell.jsx';
import BackButton from './components/BackButton.jsx';
import CartBar from './components/CartBar.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import AdminRoute from './components/AdminRoute.jsx';
import DriverRoute from './components/DriverRoute.jsx';
import HomeRoute from './components/HomeRoute.jsx';
import { HeroChromeProvider, useHeroChrome } from './context/HeroChrome.jsx';
import { useTheme } from './context/ThemeContext.jsx';
import { isCustomerApp, isAdminApp } from './lib/appTarget.js';
import { primeLocationPermission } from './lib/geo.js';
import Button from './components/Button.jsx';
import { PageSpinner } from './components/Spinner.jsx';
import { useAuth } from './context/AuthContext.jsx';
import { useCatalogConfig } from './hooks/useCatalog.js';

// Public pages are the first-paint path, so they're bundled eagerly-lazy:
// still split, but never missing.
const Landing = lazy(() => import('./pages/public/Landing.jsx'));
const Onboarding = lazy(() => import('./pages/public/Onboarding.jsx'));
const Catalog = lazy(() => import('./pages/public/Catalog.jsx'));
const ProductDetail = lazy(() => import('./pages/public/ProductDetail.jsx'));
const SampleBox = lazy(() => import('./pages/public/SampleBox.jsx'));
const Checkout = lazy(() => import('./pages/public/Checkout.jsx'));
const Cart = lazy(() => import('./pages/public/Cart.jsx'));
const Login = lazy(() => import('./pages/public/Login.jsx'));
const Legal = lazy(() => import('./pages/public/Legal.jsx'));
const DeleteAccountPage = lazy(() => import('./pages/public/DeleteAccountPage.jsx'));
const AppPay = lazy(() => import('./pages/public/AppPay.jsx'));
const Register = lazy(() => import('./pages/public/Register.jsx'));
const CheckoutResult = lazy(() => import('./pages/public/CheckoutResult.jsx'));

/**
 * The members and admin areas are authored in parallel with this shell.
 *
 * `import.meta.glob` is what makes that safe: it resolves to a map of the
 * files that exist at build time, so a page that hasn't landed yet is simply
 * absent from the map instead of being an unresolvable import that fails the
 * build. A missing page renders a placeholder; every other route still works.
 */
const memberModules = import.meta.glob('./pages/members/*.jsx');
const adminModules = import.meta.glob('./pages/admin/*.jsx');
const driverModules = import.meta.glob('./pages/driver/*.jsx');

function Placeholder({ name, area }) {
  return (
    <div className="shell flex min-h-[60vh] flex-col items-center justify-center py-20 text-center">
      <span className="eyebrow">{area}</span>
      <h1 className="mt-3 font-display text-3xl text-heading">{name} is on the way</h1>
      <p className="mt-3 max-w-md text-sm text-ink-muted">
        This screen is still being built. The rest of the app is unaffected.
      </p>
      <Button className="mt-8" variant="secondary" to="/">
        Back to home
      </Button>
    </div>
  );
}

/** Shown when a screen's code couldn't be downloaded (offline, or a deploy
 *  replaced it) — a load failure, never "still being built". */
function LoadFailed({ area }) {
  return (
    <div className="shell flex min-h-[60vh] flex-col items-center justify-center py-20 text-center">
      <span className="eyebrow">{area}</span>
      <h1 className="mt-3 font-display text-3xl text-heading">Couldn’t load this screen</h1>
      <p className="mt-3 max-w-md text-sm text-ink-muted">
        Check your connection and try again. If the app was just updated, reloading picks up the
        latest version.
      </p>
      <Button className="mt-8" onClick={() => window.location.reload()}>
        Reload
      </Button>
    </div>
  );
}

const RELOAD_KEY = 'hfb-chunk-reload';

function lazyPage(modules, dir, name, area) {
  const loader = modules[`./pages/${dir}/${name}.jsx`];
  return lazy(async () => {
    // Only a genuinely missing file is "still being built".
    if (!loader) return { default: () => <Placeholder name={name} area={area} /> };
    let lastError;
    // Retry once: a flaky mobile connection often succeeds a moment later.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const mod = await loader();
        if (!mod?.default) return { default: () => <Placeholder name={name} area={area} /> };
        try { sessionStorage.removeItem(RELOAD_KEY); } catch { /* storage off */ }
        return mod;
      } catch (err) {
        lastError = err;
        await new Promise((r) => setTimeout(r, 600));
      }
    }
    // Still failing: most likely this tab is running an older build whose
    // files a deploy has replaced. Reload once to fetch the current version.
    let alreadyReloaded = false;
    try {
      alreadyReloaded = sessionStorage.getItem(RELOAD_KEY) === '1';
      if (!alreadyReloaded) sessionStorage.setItem(RELOAD_KEY, '1');
    } catch { /* storage off → just show the error */ alreadyReloaded = true; }
    if (!alreadyReloaded) {
      window.location.reload();
      return new Promise(() => {}); // keep the spinner up while the page reloads
    }
    console.error(`[app] could not load ${name}`, lastError);
    return { default: () => <LoadFailed area={area} /> };
  });
}

const MembersDashboard = lazyPage(memberModules, 'members', 'MembersDashboard', 'Members area');
const SubscriptionManage = lazyPage(memberModules, 'members', 'SubscriptionManage', 'Members area');
const DeliveryCalendar = lazyPage(memberModules, 'members', 'DeliveryCalendar', 'Members area');
const OrderHistory = lazyPage(memberModules, 'members', 'OrderHistory', 'Members area');
const ProfileSettings = lazyPage(memberModules, 'members', 'ProfileSettings', 'Members area');
const Wallet = lazyPage(memberModules, 'members', 'Wallet', 'Members area');

const AdminOverview = lazyPage(adminModules, 'admin', 'AdminOverview', 'Admin');
const AdminProducts = lazyPage(adminModules, 'admin', 'AdminProducts', 'Admin');
const AdminDeliveries = lazyPage(adminModules, 'admin', 'AdminDeliveries', 'Admin');
const AdminLiveDrivers = lazyPage(adminModules, 'admin', 'AdminLiveDrivers', 'Admin');
const AdminCoupons = lazyPage(adminModules, 'admin', 'AdminCoupons', 'Admin');
const AdminCustomers = lazyPage(adminModules, 'admin', 'AdminCustomers', 'Admin');
const AdminSettings = lazyPage(adminModules, 'admin', 'AdminSettings', 'Admin');
const AdminAnnouncements = lazyPage(adminModules, 'admin', 'AdminAnnouncements', 'Admin');
const AdminStock = lazyPage(adminModules, 'admin', 'AdminStock', 'Admin');
const AdminExpenses = lazyPage(adminModules, 'admin', 'AdminExpenses', 'Admin');
const AdminReports = lazyPage(adminModules, 'admin', 'AdminReports', 'Admin');
const AdminPayouts = lazyPage(adminModules, 'admin', 'AdminPayouts', 'Admin');
const AdminReviews = lazyPage(adminModules, 'admin', 'AdminReviews', 'Admin');
const AdminAppAdmin = lazyPage(adminModules, 'admin', 'AdminAppAdmin', 'App admin');

const DriverDashboard = lazyPage(driverModules, 'driver', 'DriverDashboard', 'Driver');
const DriverWallet = lazyPage(driverModules, 'driver', 'DriverWallet', 'Driver');
const DriverAccount = lazyPage(driverModules, 'driver', 'DriverAccount', 'Driver');

function NotFound() {
  return (
    <div className="shell flex min-h-[60vh] flex-col items-center justify-center py-20 text-center">
      <span className="text-4xl" aria-hidden="true">
        🍊
      </span>
      <h1 className="mt-4 font-display text-3xl text-heading">We couldn&apos;t find that page</h1>
      <p className="mt-3 max-w-md text-sm text-ink-muted">
        The link may be out of date. Everything we deliver is in the shop.
      </p>
      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <Button to="/products">Browse the shop</Button>
        <Button variant="secondary" to="/">
          Back to home
        </Button>
      </div>
    </div>
  );
}

/**
 * Native + theme chrome that has no UI of its own:
 *  - keeps the Android status-bar icons light while a dark hero or dark theme is
 *    on screen (and dark on the light screens), matching the bar behind them;
 *  - applies the admin's app theme to everyone.
 */
function AppChrome() {
  const { heroActive } = useHeroChrome();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Push notifications: refresh the bell when one arrives, and open the right
  // screen when one is tapped from the notification tray.
  useEffect(() => {
    const onNotification = (e) => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      // e.g. "You're approved!" — the driver gate must re-check at once.
      queryClient.invalidateQueries({ queryKey: ['partner-me'] });
      queryClient.invalidateQueries({ queryKey: ['driver-wallet'] });
      // A new offer or assignment: show it now rather than at the next poll.
      queryClient.invalidateQueries({ queryKey: ['driver-offers'] });
      queryClient.invalidateQueries({ queryKey: ['driver-route'] });
      const link = e.detail?.link;
      if (typeof link === 'string' && link.startsWith('/')) navigate(link);
    };
    window.addEventListener('hfb:notification', onNotification);
    return () => window.removeEventListener('hfb:notification', onNotification);
  }, [navigate, queryClient]);

  // In the native apps, coming back from the background counts as "focus", so
  // queries that opt into refetchOnWindowFocus (driver status, inbox) refresh
  // on resume instead of showing what was on screen when the app was left.
  useEffect(() => {
    if (!window.Capacitor?.isNativePlatform?.()) return undefined;
    let handle;
    import('@capacitor/app')
      .then(({ App }) => App.addListener('appStateChange', ({ isActive }) => {
        focusManager.setFocused(isActive);
        if (isActive) queryClient.invalidateQueries({ queryKey: ['partner-me'] });
      }))
      .then((h) => { handle = h; })
      .catch(() => {});
    return () => handle?.remove?.();
  }, [queryClient]);
  const { isDark, isMint, resolvedTheme, hasLightBand, setDefaultTheme } = useTheme();
  const { data: config } = useCatalogConfig();

  // The admin's theme applies to everyone — there is no per-user choice.
  useEffect(() => {
    if (config?.defaultTheme) setDefaultTheme(config.defaultTheme);
  }, [config?.defaultTheme, setDefaultTheme]);

  // Ask for location once, at start-up, on the native apps. Both apps need it —
  // customers to pin their door, drivers to share their run — and asking here
  // means the OS prompt arrives with the rest of the first-run experience
  // instead of interrupting a checkout. A refusal is fine: the map can still be
  // tapped, and the driver's duty toggle asks again when it actually matters.
  useEffect(() => {
    // The admin app declares no location permission and never needs one, so
    // asking there would only produce a request the OS refuses outright.
    if (isAdminApp) return;
    primeLocationPermission().catch(() => {});
  }, []);

  // Native status bar: light icons only when a genuinely dark bar is on screen —
  // the dark Orchard hero (not the light Mint one) or the dark theme.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (cancelled || !Capacitor?.isNativePlatform?.()) return;
        const { StatusBar, Style } = await import('@capacitor/status-bar');
        // Mint and Sunrise have LIGHT hero bands; Pop's band is bold green, so
        // it wants light icons like the default Orchard band.
        const heroDark = heroActive && !hasLightBand;
        const onDark = heroDark || isDark;
        // Capacitor's naming is inverted: Style.Dark = LIGHT icons (for dark
        // backgrounds), Style.Light = DARK icons (for light backgrounds).
        await StatusBar.setStyle({ style: onDark ? Style.Dark : Style.Light });
        if (Capacitor.getPlatform() === 'android') {
          // Match the bar to whatever is actually behind it in this theme.
          const BAND = { pop: '#0B7A4B' };
          const GROUND = { mint: '#E9F5F0', sunrise: '#FFF6E6', pop: '#F2FBF6' };
          const color = heroDark
            ? (BAND[resolvedTheme] ?? '#0E3B22')
            : isDark
              ? '#16241C'
              : (GROUND[resolvedTheme] ?? '#FFFFFF');
          await StatusBar.setBackgroundColor({ color });
        }
      } catch {
        /* status-bar plugin unavailable (web / not installed) — no-op */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [heroActive, isDark, isMint, resolvedTheme, hasLightBand]);

  return null;
}

/** Route changes should start at the top, not wherever the last page was. */
/**
 * Every new screen starts at the top. The old version asked for behavior
 * 'auto', which the stylesheet's `scroll-behavior: smooth` turned into an
 * animation — and that animation was cut short when the next (lazy-loaded)
 * screen rendered and changed the page height, leaving it half-way down.
 * Now it jumps instantly (smooth scrolling paused for the jump) and jumps
 * again once the new screen has painted. Going BACK keeps where you were.
 */
function jumpToTop() {
  const root = document.documentElement;
  const prev = root.style.scrollBehavior;
  root.style.scrollBehavior = 'auto';
  window.scrollTo(0, 0);
  root.scrollTop = 0;
  document.body.scrollTop = 0;
  root.style.scrollBehavior = prev;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    if (navType === 'POP') return; // back/forward: leave the position alone
    jumpToTop();
    // Again after the new screen (often lazy-loaded) has rendered.
    const raf = requestAnimationFrame(jumpToTop);
    const late = setTimeout(jumpToTop, 250);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(late);
    };
  }, [pathname, navType]);
  return null;
}

export default function App() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  // The admin area has its own full chrome (AdminShell: sidebar + top bar), so
  // the public Header/Footer and the member BottomNav are hidden there to avoid
  // a redundant double header/nav. The driver and admin APPS (build targets)
  // are role tools with no storefront, so they hide the customer chrome
  // everywhere too.
  const isAdminArea = pathname.startsWith('/admin');
  // /pay runs Paytm inside the native app's browser — no site chrome there.
  const hideCustomerChrome = isAdminArea || !isCustomerApp || pathname === '/pay';

  return (
    <HeroChromeProvider>
    <div className="pad-safe-x flex min-h-screen flex-col">
      <ScrollToTop />
      <AppChrome />
      <BackButton />
      <PullToRefresh />
      {!hideCustomerChrome && <Header />}

      {/*
        The tab bar is fixed over the page on phones, so signed-in users get
        matching bottom padding — otherwise the last row of every list sits
        permanently under it.
      */}
      <main className={`flex-1 ${user && !hideCustomerChrome ? 'pb-24 sm:pb-0' : ''}`}>
        <ErrorBoundary>
          <Suspense fallback={<PageSpinner />}>
            <Routes>
              {/* Public */}
              <Route path="/" element={<HomeRoute><Landing /></HomeRoute>} />
              <Route path="/products" element={<Catalog />} />
              <Route path="/products/:slug" element={<ProductDetail />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/sample" element={<SampleBox />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/checkout/result" element={<CheckoutResult />} />
              <Route path="/login" element={<Login />} />
              {/* Legal — the URLs the app-store listings point at. Public, so
                  they open for anyone, signed in or not, in every build. */}
              <Route path="/privacy" element={<Legal page="privacy" />} />
              <Route path="/terms" element={<Legal page="terms" />} />
              <Route path="/delete-account" element={<DeleteAccountPage />} />
              <Route path="/account-deleted" element={<DeleteAccountPage done />} />
              <Route path="/pay" element={<AppPay />} />
              <Route path="/register" element={<Register />} />
              <Route path="/onboard" element={<Onboarding />} />

              {/* Members */}
              <Route element={<ProtectedRoute />}>
                <Route path="/app" element={<MembersDashboard />} />
                <Route path="/app/subscriptions/:id" element={<SubscriptionManage />} />
                <Route path="/app/subscriptions/:id/calendar" element={<DeliveryCalendar />} />
                <Route path="/app/orders" element={<OrderHistory />} />
                {/* upcoming | past — the delivery dates behind an order */}
                <Route path="/app/orders/:view" element={<OrderHistory />} />
                <Route path="/app/wallet" element={<Wallet />} />
                <Route path="/app/profile" element={<ProfileSettings />} />
              </Route>

              {/* Driver (delivery boys). In the driver build these sit inside
                  DriverShell, which supplies the app bar and tab bar the
                  storefront chrome doesn't cover. */}
              <Route element={<DriverRoute />}>
                <Route path="/driver" element={<DriverShell><DriverDashboard /></DriverShell>} />
                <Route
                  path="/driver/notifications"
                  element={
                    <DriverShell>
                      {/* The driver app has tabs; only the customer build needs
                          a way back out of the inbox. */}
                      <NotificationsInbox backTo={isCustomerApp ? '/driver' : null} />
                    </DriverShell>
                  }
                />
                <Route path="/driver/wallet" element={<DriverShell><DriverWallet /></DriverShell>} />
                <Route path="/driver/account" element={<DriverShell><DriverAccount /></DriverShell>} />
              </Route>

              {/* Admin */}
              <Route element={<AdminRoute />}>
                <Route path="/admin" element={<AdminOverview />} />
                <Route path="/admin/products" element={<AdminProducts />} />
                <Route path="/admin/deliveries" element={<AdminDeliveries />} />
                <Route path="/admin/live" element={<AdminLiveDrivers />} />
                <Route path="/admin/coupons" element={<AdminCoupons />} />
                <Route path="/admin/customers" element={<AdminCustomers />} />
                <Route path="/admin/reviews" element={<AdminReviews />} />
                <Route path="/admin/announcements" element={<AdminAnnouncements />} />
                <Route path="/admin/stock" element={<AdminStock />} />
                <Route path="/admin/expenses" element={<AdminExpenses />} />
                <Route path="/admin/payouts" element={<AdminPayouts />} />
                <Route path="/admin/reports" element={<AdminReports />} />
                <Route path="/admin/settings" element={<AdminSettings />} />
                <Route path="/admin/notifications" element={<NotificationsInbox />} />
                <Route path="/admin/app" element={<AdminAppAdmin />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>

      {!hideCustomerChrome && <Footer />}
      {!hideCustomerChrome && <BottomNav />}
      {/* Floating cart bar — everywhere except the cart/checkout it links to. */}
      {!hideCustomerChrome && !pathname.startsWith('/checkout') && pathname !== '/cart' && <CartBar />}
    </div>
    </HeroChromeProvider>
  );
}
